import {
  ProjectDecisionWord,
  ProjectMilestoneState,
  ProjectMilestoneSwitchBlocker,
  ProjectState,
  ProjectRoomAvailability,
  ProjectRoomKey,
  ProjectTargetFit,
  ProjectTier,
  ProjectTrackForecastKind,
  ProjectTrackMessageCode,
  ProjectTrackMessageTone,
  ProjectTrackMoveKind,
  ProjectTrackSegmentKind,
  ProjectTrackState,
  ProjectWriteBlocker,
} from 'models/projects/projectEnums';
import Dashboard from 'models/dashboard';
import type { PersonRef } from 'models/projects/projectRoomParts';
import { areDifferent } from 'utils/array';

// Project aggregate (domain). GET projects/{id} returns package milestones in package order; labels
// come from each milestone or step task name.

/** One milestone step: platform task, name, and sort order. Its guide is the task's description (task read). */
export class ProjectStep {
  taskId: number;

  /** Task name; the client does not supply a separate step label. */
  name: string;

  sortOrder: number;

  completed: boolean;

  completedDate?: Date;

  /** The seed's step key; undefined for a step authored by hand. */
  key?: string;

  /** Under way (the API's ProjectStepDTO.Started): not completed, with a checklist item ticked, the task Accepted or InProgress. */
  started: boolean;

  constructor() {
    this.taskId = 0;
    this.name = '';
    this.sortOrder = 0;
    this.completed = false;
    this.started = false;
  }
}

/** What was done in the external audit room (its part `externalAudit`): each moment with when and by whom. */
export class ProjectExternalAuditStep {
  quoteRequestedOn?: Date;

  quoteRequestedBy?: PersonRef;

  /** The quote was requested through ISOPlanner: it is not taken back. */
  requestedThroughIsoplanner: boolean;

  quoteSignedOn?: Date;

  quoteSignedBy?: PersonRef;

  /** The organisation arranges the audit without ISOPlanner: the quote and its signature in one. */
  arrangedElsewhereOn?: Date;

  arrangedElsewhereBy?: PersonRef;

  stage1On?: Date;

  /** The audit date; set once the dates are recorded. */
  stage2On?: Date;

  skippedOn?: Date;

  skippedBy?: PersonRef;

  skipReason?: string;

  /** The room may be confirmed: it is skipped, or the audit date is recorded. */
  done: boolean;

  /** Where the room stands, the server's: the first moment not passed yet, or done. */
  moment: ProjectExternalAuditMoment;

  /** What an undo takes back now, as the server allows it; undefined when nothing. */
  undo?: ProjectExternalAuditUndo;

  constructor() {
    this.requestedThroughIsoplanner = false;
    this.done = false;
    this.moment = 'start';
  }
}

/** Where the external audit room stands: the quote to request, to sign, the audit dates to record, or done. */
export type ProjectExternalAuditMoment = 'start' | 'requested' | 'signed' | 'done';

export const projectExternalAuditMoments: readonly ProjectExternalAuditMoment[] = [
  'start',
  'requested',
  'signed',
  'done',
];

/** What an undo of the external audit room takes back. */
export type ProjectExternalAuditUndo = 'quote' | 'signed' | 'arrangedElsewhere' | 'dates' | 'skip';

export const projectExternalAuditUndos: readonly ProjectExternalAuditUndo[] = [
  'quote',
  'signed',
  'arrangedElsewhere',
  'dates',
  'skip',
];

/** What the external audit room offers in this environment, the same for every project. */
export class ProjectExternalAudit {
  quoteRequestEnabled: boolean;

  meetingUrl?: string;

  constructor() {
    this.quoteRequestEnabled = false;
  }
}

/**
 * One project milestone (platform task). `completed` is the task flag; `state` is the project read
 * (inProgress once a step or a room under this milestone is started or done).
 */
export class ProjectMilestone {
  /** Milestone identity: platform task id. */
  taskId: number;

  /** Milestone task name for rail and timeline labels. */
  name: string;

  /** The milestone task's own description. */
  description: string;

  completed: boolean;

  completedDate?: Date;

  state: ProjectMilestoneState;

  startDate?: Date;

  /** The milestone task's deadline; undefined when it carries none. */
  deadline?: Date;

  tasksDone: number;

  /** Zero is a fact: this milestone carries no steps. */
  tasksTotal: number;

  /** Confirmed rooms anchored under this milestone. */
  roomsDone: number;

  /** Rooms anchored under this milestone. */
  roomsTotal: number;

  /**
   * This milestone's steps in order. Populated by the detail read only; empty elsewhere means
   * not loaded.
   */
  tasks: ProjectStep[];

  /**
   * Detail-read only: why the caller cannot switch this milestone now (the server's rule: closed, not the current one,
   * or the project's write gate); undefined when they can.
   */
  switchBlocker?: ProjectMilestoneSwitchBlocker;

  /** The milestone closes by itself once every room and step under it is done; no person switches it. */
  closesWhenStepsDone: boolean;

  constructor() {
    this.taskId = 0;
    this.name = '';
    this.description = '';
    this.completed = false;
    this.state = 'todo';
    this.tasksDone = 0;
    this.tasksTotal = 0;
    this.roomsDone = 0;
    this.roomsTotal = 0;
    this.tasks = [];
    this.closesWhenStepsDone = false;
  }
}

/**
 * One timeline band: a stretch of milestone work, its dates, and its share of the project.
 * Arrives contiguous and pre-sorted.
 */
export class ProjectTrackSegment {
  /** The milestone this band is over. Undefined on a current band that names no milestone. */
  milestoneTaskId?: number;

  kind: ProjectTrackSegmentKind;

  state: ProjectMilestoneState;

  from?: Date;

  to?: Date;

  /**
   * The milestone's share of the project, 0..1. Zero on the current band, which is also drawn
   * projected.
   */
  weight: number;

  constructor() {
    this.kind = 'todo';
    this.state = 'todo';
    this.weight = 0;
  }
}

/** The track message: code and tone. */
export class ProjectTrackMessage {
  tone: ProjectTrackMessageTone;

  code: ProjectTrackMessageCode;

  constructor() {
    this.tone = 'quiet';
    this.code = 'onTrack';
  }
}

/**
 * One next action and its worth in days. `impactDays` 0 is a real measurement (moves the
 * forecast by nothing); undefined means no date impact at all.
 */
export class ProjectTrackMove {
  kind: ProjectTrackMoveKind;

  /** The milestone this move is about. Undefined on a move about the target date. */
  milestoneTaskId?: number;

  taskId?: number;

  /** The room to open, by key. Undefined unless the move names one. */
  roomKey?: string;

  impactDays?: number;

  /** True for the move a rail highlights. At most one move is marked primary. */
  primary: boolean;

  /** What is still open in the room an openRoom move names, most important first; empty for every other move. */
  todo: ProjectRoomTodo[];

  constructor() {
    this.kind = 'markStageDone';
    this.primary = false;
    this.todo = [];
  }
}

/** One thing still open in a room, from the room's own read: what it is (a projectRoomTodoKey) and how many. */
export class ProjectRoomTodo {
  key: string;

  count: number;

  constructor(key: string, count: number) {
    this.key = key;
    this.count = count;
  }
}

/**
 * Projected completion versus the owner's target date, and the recommended next action. Every
 * date is the server's forecast, computed against `today`. The web must not derive dates from its
 * own clock instead of `today`.
 */
export class ProjectTrack {
  state: ProjectTrackState;

  doneCount: number;

  /** Total milestone count in the journey, as authored by the package. */
  totalCount: number;

  /** The date the forecast measures from. Undefined until the first target date started the project. */
  startDate?: Date;

  /** The instant this track was computed for: the reference clock for every other date here. */
  today: Date;

  targetDate?: Date;

  forecastKind: ProjectTrackForecastKind;

  /**
   * The first day of the month containing the forecast date. Undefined when there is no
   * forecast.
   */
  forecastMonth?: Date;

  /** The P50 completion day. Undefined when there is no forecast. */
  forecastDate?: Date;

  /** Whole days the forecast is past what it is compared against; negative is early. */
  slipDays?: number;

  /** How the target date compares with the forecast; `none` without a target or forecast. */
  targetFit: ProjectTargetFit;

  idleDays: number;

  segments: ProjectTrackSegment[];

  /** Undefined when the track has nothing to say. */
  message?: ProjectTrackMessage;

  moves: ProjectTrackMove[];

  /** The server's hash of the facts this track came from. Held, never rendered. */
  stateHash: string;

  constructor() {
    this.state = 'notStarted';
    this.doneCount = 0;
    this.totalCount = 0;
    this.today = new Date(0);
    this.forecastKind = 'none';
    this.targetFit = 'none';
    this.idleDays = 0;
    this.segments = [];
    this.moves = [];
    this.stateHash = '';
  }

  /** Returns the primary move a rail highlights, or undefined when no move is marked primary. */
  getPrimaryMove(): ProjectTrackMove | undefined {
    return this.moves.find((move) => move.primary === true);
  }
}

/**
 * Room availability and dispatch metadata. Populated by GET projects/{id} or create; one row per
 * registered room. A short list means not loaded, not fewer rooms.
 */
export class ProjectRoomAvailabilityInfo {
  /** The room's key: its identity, and the `/room/{roomKey}` route segment. */
  roomKey: ProjectRoomKey;

  /** The EntityTypes value this room covers. An attribute of the room, not its identity. */
  kind: number;

  availability: ProjectRoomAvailability;

  /** The decision routes this room dispatches, as the server declared them. */
  actions: ProjectDecisionWord[];

  /** True when a decision about one of this room's items may carry an editedDescription. */
  supportsEditDescription: boolean;

  /** This room's anchor task; undefined when the project anchors none. */
  deliverableTaskId?: number;

  /** The anchor task's authored name; renderer identity is roomKey. */
  name?: string;

  /**
   * The milestone this room's anchor hangs under. Undefined when this project anchors no such
   * room.
   */
  milestoneTaskId?: number;

  sortOrder: number;

  confirmed: boolean;

  /**
   * When the anchor task was ticked. Undefined when unconfirmed, or even confirmed but undated.
   * `confirmed` is the fact; this is a receipt detail only.
   */
  confirmedDate?: Date;

  /** The anchor task's deadline. Read only once `track.startDate` is set. */
  deadline?: Date;

  /** False for a room that is done when its data says so; such a room never offers a confirm. */
  confirmable: boolean;

  /**
   * The room's progress from its own data. Only on a live, confirmable room that is not confirmed and measures it.
   */
  progress?: ProjectRoomProgress;

  /** Only the confirm is left, as the server decides it. Read this rather than deriving it from `progress`. */
  readyToConfirm: boolean;

  /**
   * The room is done, as the server decides it from stored facts. Read this rather than deriving it from `confirmed`
   * and `progress`.
   */
  done: boolean;

  /** Work in the room is recorded, as the server decides it from stored facts. */
  started: boolean;

  constructor() {
    this.roomKey = '';
    this.kind = 0;
    this.availability = 'notSeeded';
    this.actions = [];
    this.supportsEditDescription = false;
    this.sortOrder = 0;
    this.confirmed = false;
    this.confirmable = true;
    this.readyToConfirm = false;
    this.done = false;
    this.started = false;
  }
}

/**
 * A room's progress from its own data: `done` of `total`. Total 0 is not started; `done` equal to a
 * `total` above 0 is done.
 */
export class ProjectRoomProgress {
  done: number;

  total: number;

  constructor() {
    this.done = 0;
    this.total = 0;
  }
}

/** This organization's Project tier and what follows from it. Never derived client-side. */
export class ProjectTierInfo {
  tier: ProjectTier;

  /** False on an expired trial alone; every purchased tier writes. */
  writesOpen: boolean;

  trialExpired: boolean;

  // Package fields: per-project query only; undefined means not queried.

  /** The project's package. Undefined on a package-less project and on the tenant-wide read. */
  packageId?: number;

  packageName?: string;

  /** PackageTenantStates: 3 activated, 99 installing, 100 content upload. */
  packageState?: number;

  /** Whole days left in the trial window. Undefined off a trial. */
  trialDaysLeft?: number;

  /** The master a preview package upgrades to. The three arrive together or not at all. */
  upgradePackageId?: number;

  upgradePackageName?: string;

  upgradePrice?: number;

  /**
   * The newest Live version of this project's package (same code, higher version). Undefined when
   * there is none. Updating is installing that version through the Store.
   */
  updatePackageId?: number;

  updatePackageName?: string;

  /** The newer version's version string, for example "1.1". */
  updatePackageVersion?: string;

  /**
   * PackageTenantStates of this organization on the newer version. Undefined when it holds no row:
   * the version has to be assigned or ordered first.
   */
  updatePackageState?: number;

  constructor() {
    this.tier = 'trial';
    this.writesOpen = false;
    this.trialExpired = false;
  }
}

/**
 * One project as the list read (GET projects) returns it: no milestones, rooms, tier, track or
 * dashboard. Read those from a `Project` of GET projects/{id}.
 */
export class ProjectSummary {
  projectId: number;

  name: string;

  description: string;

  state: ProjectState;

  /** True when every milestone is done; server-derived. */
  isCompleted: boolean;

  /**
   * The package this project came from. Undefined on an authoring project and on a project created
   * before packages were required, not 0.
   */
  packageId?: number;

  /** False when an owned package carries this project; Delete is not offered. */
  canDelete?: boolean;

  /**
   * The authorization schema this project is narrowed by; undefined when every member of the
   * organization can see it. Assigned through the auth schema usage screen, never from here.
   */
  authSchemaId?: number;

  normIds: number[];

  /**
   * The tags on this project - display and grouping only. Undefined means the read did not
   * carry any.
   */
  tagIds?: number[];

  /** User target date (goal, not a gate). Undefined when unset. */
  targetDate?: Date;

  created?: Date;

  constructor() {
    this.projectId = -1;
    this.name = '';
    this.description = '';
    this.state = ProjectState.active;
    this.isCompleted = false;
    this.normIds = [];
  }
}

export default class Project extends ProjectSummary {
  /** Project-specific tier, populated by the detail read. */
  tier?: ProjectTierInfo;

  /** Detail-read only. Empty on a mutation response means not loaded - see `hasDetail`. */
  milestones: ProjectMilestone[];

  /**
   * Detail-read only, one row per registered room. Empty on a mutation response means not
   * loaded.
   */
  rooms: ProjectRoomAvailabilityInfo[];

  /**
   * Overview widget host. Undefined until POST projects/{id}/dashboard creates it, or on a
   * mutation response.
   */
  dashboard?: Dashboard;

  /** The day the work starts: the named start date, or the creation date. Detail-read only. */
  startDate?: Date;

  /**
   * Forecast and recommended moves. Detail-read only; undefined means not loaded. GET
   * projects/{id} populates it.
   */
  track?: ProjectTrack;

  /** Detail-read only: what the external audit step offers in this environment. */
  externalAudit?: ProjectExternalAudit;

  /**
   * Detail-read only: why the caller cannot write the project now (a step's completion, a milestone switch), the
   * server's write gate; undefined when they can.
   */
  writeBlocker?: ProjectWriteBlocker;

  /**
   * True when the read populated `milestones`, `rooms`, and `track` (GET projects/{id}). When
   * false, empty collections mean not loaded. Do not render rail, timeline, or confirmed counts
   * from a mutation response.
   */
  readonly hasDetail: boolean;

  constructor(hasDetail: boolean = false) {
    super();
    this.milestones = [];
    this.rooms = [];
    this.hasDetail = hasDetail;
  }

  /** Resolve the authored task label without interpreting or translating the routing key. */
  getRoomName(roomKey: string | undefined): string {
    return this.rooms.find((room) => room.roomKey === roomKey)?.name ?? '';
  }

  /**
   * This project with one step's `started` set, as the step page reads it from the task it just wrote, ahead of the
   * project read that follows; the project itself when the step already reads so or is not in it.
   */
  withStepStarted(taskId: number, started: boolean): Project {
    const holder = this.milestones.find((milestone) =>
      milestone.tasks.some((task) => task.taskId === taskId && task.started !== started),
    );
    if (holder === undefined) return this;

    const milestones = this.milestones.map((milestone) =>
      milestone !== holder
        ? milestone
        : Object.assign(new ProjectMilestone(), milestone, {
            tasks: milestone.tasks.map((task) =>
              task.taskId === taskId ? Object.assign(new ProjectStep(), task, { started: started }) : task,
            ),
          }),
    );

    return Object.assign(new Project(this.hasDetail), this, { milestones: milestones });
  }

  /** Dirty check over user-movable fields, including `hasDetail`. */
  isEqual(item: Project): boolean {
    let isEqual: boolean = true;
    if (item.projectId !== this.projectId) isEqual = false;
    if (item.name !== this.name) isEqual = false;
    if (item.description !== this.description) isEqual = false;
    if (item.state !== this.state) isEqual = false;
    if (item.isCompleted !== this.isCompleted) isEqual = false;
    if (item.packageId !== this.packageId) isEqual = false;
    if (item.authSchemaId !== this.authSchemaId) isEqual = false;
    if (item.hasDetail !== this.hasDetail) isEqual = false;
    if (item.targetDate?.getTime() !== this.targetDate?.getTime()) isEqual = false;

    if (areDifferent(item.normIds, this.normIds, (a: number, b: number) => a === b) === true) {
      isEqual = false;
    }

    if (
      areDifferent(
        item.milestones,
        this.milestones,
        (a: ProjectMilestone, b: ProjectMilestone) => a.taskId === b.taskId && a.completed === b.completed,
      ) === true
    ) {
      isEqual = false;
    }

    if (
      areDifferent(
        item.rooms,
        this.rooms,
        (a: ProjectRoomAvailabilityInfo, b: ProjectRoomAvailabilityInfo) =>
          a.roomKey === b.roomKey &&
          a.name === b.name &&
          a.availability === b.availability &&
          a.confirmed === b.confirmed &&
          a.readyToConfirm === b.readyToConfirm &&
          a.done === b.done &&
          a.started === b.started,
      ) === true
    ) {
      isEqual = false;
    }

    return isEqual;
  }
}

/** The Kickstart package in the Store and its trial (preview twin); both undefined without Kickstart. */
export class KickstartOffer {
  packageId?: number;

  trialPackageId?: number;
}
